
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { uniqueId } from 'lodash';
import UserModule from '@/store/modules/User';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import api from '@/service/api';
import QRCode from 'qrcode';

interface CourierEntry {
    _id: string;
    name: string;
    price: string;
}

const { $vToastify: notifier } = new Vue() as any;
const userModule = getModule(UserModule, store);

@Component
export default class Integrations extends Vue {
    @Prop({ default: true })
    public showActivator!: boolean;
    public dialogVisible = false;
    public selectedTab: number = 0;
    public currentTab: number = 0;
    public confirmCode: string = '';

    // Two factor properties
    public twoFactorDialogWaiting = false;
    public twoFactorDialogVisible = false;
    public twoFactorType: 'totp' | 'email' | null = null;
    public isEmail2FACooldownActive = false;
    public email2FACooldownTime = 0;

    // Courier integration properties
    public courierIntegrationEnabled = false;
    public courierEntries: CourierEntry[] = [
        { _id: uniqueId(), name: '', price: '' },
    ];

    // Save functionality
    public savingInProgress = false;

    // Validation rules
    public priceRules = [
        (v: string) => {
            if (v === null || v === undefined || v === '') { return 'Pole wymagane'; }
            const num = parseFloat(v);
            if (isNaN(num)) { return 'Wartość musi być liczbą'; }
            if (num <= 0) { return 'Wartość musi być większa od 0'; }
            return true;
        },
    ];

    public nameRules = [
        (v: string) => {
            if (v === null || v === undefined || v === '') { return 'Pole wymagane'; }
            return true;
        },
    ];
    private email2FACooldownInterval: number | null = null;
    private originalSettings: { enabled: boolean; entries: CourierEntry[] } = {
        enabled: false,
        entries: [],
    };

    get activeTwoFactorMethod(): 'totp' | 'email' | null {
        const user = userModule.user;
        if (!user || !user.twoFactorEnabled) {
            return null;
        }
        return user.twoFactorMethod;
    }

    get activeTwoFactorLabel() {
        if (this.activeTwoFactorMethod === 'totp') {
            return 'authenticator';
        }
        if (this.activeTwoFactorMethod === 'email') {
            return 'mail';
        }
        return '';
    }

    get isTotp2FAActive() {
        return this.activeTwoFactorMethod === 'totp';
    }

    get isEmail2FAActive() {
        return this.activeTwoFactorMethod === 'email';
    }

    get isTotp2FASwitchActive() {
        return this.isTotp2FAActive || (this.pendingTwoFactorMethod === 'totp' && (this.twoFactorDialogVisible || this.twoFactorDialogWaiting));
    }

    get isEmail2FASwitchActive() {
        return this.isEmail2FAActive || (this.pendingTwoFactorMethod === 'email' && this.twoFactorDialogVisible);
    }

    get isDirty() {
        if (this.courierIntegrationEnabled !== this.originalSettings.enabled) {
            return true;
        }

        if (this.courierEntries.length !== this.originalSettings.entries.length) {
            return true;
        }

        return this.courierEntries.some((entry, index) => {
            const original = this.originalSettings.entries[index];
            return !original || entry.name !== original.name || entry.price !== original.price;
        });
    }
    public integrationsTwofaDigits = ['', '', '', '', '', ''];

    public pendingTwoFactorMethod: 'totp' | 'email' | null = null;
    public twoFactorCodeErrors: string[] = [];

    public mounted() {
        this.saveCurrentAsOriginal();
        if (!this.showActivator) {
            this.$root.$on('openIntegrations', this.onOpenIntegrations);
        }
    }

    public beforeDestroy() {
        if (!this.showActivator) {
            this.$root.$off('openIntegrations', this.onOpenIntegrations);
        }
        if (this.email2FACooldownInterval) {
            window.clearInterval(this.email2FACooldownInterval);
            this.email2FACooldownInterval = null;
        }
    }

    public onOpenIntegrations(payload?: { tab?: number }) {
        this.dialogVisible = true;
        if (payload?.tab !== undefined) {
            this.selectedTab = payload.tab;
            this.currentTab = payload.tab;
        }
    }

    public async saveCourierSettings() {
        if (this.savingInProgress) { return; }

        this.savingInProgress = true;

        try {
            // Here you would typically call an API to save the settings
            // For now, we'll just simulate saving
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Update the original settings to match current state
            this.saveCurrentAsOriginal();

            // Show success message (if you have a notification system)
            console.log('Courier settings saved successfully');

        } catch (error) {
            console.error('Failed to save courier settings:', error);
        } finally {
            this.savingInProgress = false;
        }
    }

    public closeDialog() {
        this.dialogVisible = false;
    }

    public onTabChange() {
        this.currentTab = this.selectedTab;
    }

    public addCourierEntry() {
        this.courierEntries.push({
            _id: uniqueId(),
            name: '',
            price: '',
        });
    }

    public removeCourierEntry(index: number) {
        if (this.courierEntries.length > 1) {
            this.courierEntries.splice(index, 1);
        }
    }

    public async enableTotp() {
        this.twoFactorType = 'totp';
        this.pendingTwoFactorMethod = 'totp';
        this.twoFactorDialogWaiting = true;
        try {
            const { qrContent } = await api.enableTotp2FA();
            this.twoFactorDialogVisible = true;
            await this.$nextTick();
            QRCode.toCanvas(this.$refs.totpQr as HTMLCanvasElement, qrContent, { width: 300 });
        } catch (error) {
            this.pendingTwoFactorMethod = null;
            console.error('Failed to enable TOTP 2FA:', error);
        } finally {
            this.twoFactorDialogWaiting = false;
        }
    }

    public async confirm2FA(code: string) {
        const normalizedCode = code.replace(/\D/g, '').slice(0, 6);
        if (!/^\d{6}$/.test(normalizedCode)) {
            this.twoFactorCodeErrors = ['Kod 2FA musi składać się z 6 cyfr'];
            return;
        }

        if (!this.twoFactorType) return;
        try {
            await api.confirm2FA(normalizedCode);
            userModule.mark2FAEnabled(this.twoFactorType);
            this.twoFactorDialogVisible = false;
            if (this.twoFactorType) {
                userModule.updateUser({
                    twoFactorEnabled: true,
                    twoFactorMethod: this.twoFactorType,
                });
            }
            notifier.success({
                title: 'Sukces',
                body: this.twoFactorType === 'email'
                    ? 'Włączono 2FA - mail'
                    : 'Włączono 2FA - Authenticator',
            });
            this.pendingTwoFactorMethod = null;
            this.confirmCode = '';
            this.integrationsTwofaDigits = ['', '', '', '', '', ''];
            this.twoFactorCodeErrors = [];
        } catch (error) {
            this.twoFactorCodeErrors = ['Nieprawidłowy kod 2FA'];
        }
    }

    public openTwoFactorDialog(type: 'totp' | 'email') {
        this.twoFactorType = type;
        this.pendingTwoFactorMethod = type;
        this.twoFactorDialogVisible = true;
    }

    public onTotpSwitchChange(value: boolean) {
        if (!value || this.twoFactorDialogWaiting || this.isTotp2FAActive) {
            return;
        }
        this.enableTotp();
    }

    public onEmailSwitchChange(value: boolean) {
        if (!value || this.isEmail2FAActive) {
            return;
        }
        this.openTwoFactorDialog('email');
    }

    public closeTwoFactorDialog() {
        this.twoFactorDialogVisible = false;
        this.pendingTwoFactorMethod = null;
        this.twoFactorType = null;
        this.confirmCode = '';
        this.integrationsTwofaDigits = ['', '', '', '', '', ''];
        this.twoFactorCodeErrors = [];
    }

    public onTwoFactorDialogVisibilityChange(value: boolean) {
        this.twoFactorDialogVisible = value;
        if (!value) {
            this.pendingTwoFactorMethod = null;
            this.twoFactorType = null;
            this.confirmCode = '';
            this.integrationsTwofaDigits = ['', '', '', '', '', ''];
            this.twoFactorCodeErrors = [];
            return;
        }

        this.$nextTick(() => {
            setTimeout(() => this.focusIntegrationsTwofaDigit(0), 80);
        });
    }

    public onConfirmCodeInput(value: string) {
        const sanitizedValue = (value || '').replace(/\D/g, '').slice(0, 6);
        if (sanitizedValue !== this.confirmCode) {
            this.confirmCode = sanitizedValue;
        }
        if (this.twoFactorCodeErrors.length > 0) {
            this.twoFactorCodeErrors = [];
        }
    }

    public onIntegrationsTwofaDigitInput(index: number, event: Event) {
        const target = event.target as HTMLInputElement;
        const nextDigit = (target.value || '').replace(/\D/g, '').slice(-1);
        this.$set(this.integrationsTwofaDigits, index, nextDigit);
        this.confirmCode = this.integrationsTwofaDigits.join('');

        if (this.twoFactorCodeErrors.length > 0) {
            this.twoFactorCodeErrors = [];
        }

        if (nextDigit && index < this.integrationsTwofaDigits.length - 1) {
            this.focusIntegrationsTwofaDigit(index + 1);
        }
    }

    public onIntegrationsTwofaDigitKeydown(index: number, event: KeyboardEvent) {
        const target = event.target as HTMLInputElement;

        if (event.key === 'Backspace' && !target.value && index > 0) {
            this.focusIntegrationsTwofaDigit(index - 1);
            return;
        }

        if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            this.focusIntegrationsTwofaDigit(index - 1);
            return;
        }

        if (event.key === 'ArrowRight' && index < this.integrationsTwofaDigits.length - 1) {
            event.preventDefault();
            this.focusIntegrationsTwofaDigit(index + 1);
        }
    }

    public onIntegrationsTwofaPaste(event: ClipboardEvent) {
        const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
        if (!pasted) {
            return;
        }

        this.integrationsTwofaDigits = ['', '', '', '', '', ''];
        pasted.split('').forEach((digit, index) => {
            this.$set(this.integrationsTwofaDigits, index, digit);
        });
        this.confirmCode = this.integrationsTwofaDigits.join('');

        if (this.twoFactorCodeErrors.length > 0) {
            this.twoFactorCodeErrors = [];
        }

        const focusIndex = Math.min(pasted.length, this.integrationsTwofaDigits.length - 1);
        this.focusIntegrationsTwofaDigit(focusIndex);
    }

    private focusIntegrationsTwofaDigit(index: number) {
        this.$nextTick(() => {
            const inputRef = this.$refs[`integrationsTwofaDigit${index}`] as HTMLInputElement[] | HTMLInputElement | undefined;
            const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;
            input?.focus?.();
            input?.select?.();
        });
    }

    public async sendEmailCode2FA() {
        if (this.isEmail2FACooldownActive) { return; }

        try {
            await api.enableEmail2FA();
        } catch (error) {
            console.error('Failed to send Email 2FA code:', error);
            notifier.error({
                title: 'Błąd',
                body: 'Nie udało się wysłać kodu 2FA mailem',
            });
            return;
        }

        this.isEmail2FACooldownActive = true;
        this.email2FACooldownTime = 30;

        notifier.success({
            title: 'Wysłano',
            body: 'Kod wysłano mailem',
        });

        this.email2FACooldownInterval = window.setInterval(() => {
            this.email2FACooldownTime--;
            if (this.email2FACooldownTime <= 0) {
                this.isEmail2FACooldownActive = false;
                if (this.email2FACooldownInterval) {
                    window.clearInterval(this.email2FACooldownInterval);
                    this.email2FACooldownInterval = null;
                }
            }
        }, 1000);
    }

    private saveCurrentAsOriginal() {
        this.originalSettings = {
            enabled: this.courierIntegrationEnabled,
            entries: this.courierEntries.map((entry) => ({ ...entry })),
        };
    }
}
